import { useCallback, useRef, useState } from 'react'

import {
  POST_SURVEY,
  convertParams,
  convertResult,
} from 'app/common/api/requests/food/surveys/post'
import type { CreateSurveyParams, Survey } from 'app/common/types/surveys'
import { api } from 'app/api'
import {
  CHECK_SURVEY_OVERLAP,
  SurveyOverlapResult,
  convertParams as overlapParams,
} from 'app/common/api/requests/food/surveys/check_overlap/post'
import usePopupErr from 'app/common/hooks/usePopupErr'
import useCallbackRef from 'app/common/hooks/useCallbackRef'

import { formatSurveyOverlaps, getSurveyErrorMessage } from '../getSurveyErrorMessage'

type Options = {
  onSuccess?: (survey: Survey) => void
}

export const useCreateSurvey = ({ onSuccess }: Options = {}) => {
  const [pending, setPending] = useState<boolean>(false)
  const [popupErr] = usePopupErr()
  const inFlight = useRef(false)
  const onSuccessRef = useCallbackRef(onSuccess)

  const submit = useCallback(
    async (params: CreateSurveyParams) => {
      if (inFlight.current) return false
      inFlight.current = true
      setPending(true)
      try {
        const check = await api.request<CreateSurveyParams, SurveyOverlapResult>(
          CHECK_SURVEY_OVERLAP,
          {
            params,
            error: error => getSurveyErrorMessage(error, 'Не удалось проверить пересечение периодов опросов'),
            convertions: { convertParams: overlapParams },
          },
        )
        if (!check) return false
        if (check.has_overlaps) {
          popupErr(
            `Обнаружено пересечение периодов опроса:\n${formatSurveyOverlaps(check.overlaps)}`,
          )
          return false
        }

        const res = await api.request(POST_SURVEY, {
          error: error => getSurveyErrorMessage(error, 'Не удалось создать опрос'),
          params,
          convertions: {
            convertParams,
            convertResult,
          },
        })

        if (res) {
          onSuccessRef.current?.(res)
          return res
        }
        return false
      } finally {
        inFlight.current = false
        setPending(false)
      }
    },
    [onSuccessRef, popupErr],
  )

  return {
    pending,
    submit,
  }
}
